import * as React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { isNull } from 'lodash';

import { ArrowRightIcon, CheckIcon, EllipsisIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  Button,
  Col,
  Divider,
  Drawer,
  Row,
  Spinner,
  Typography,
} from '@revfluence/fresh';
import { STAPaymentGroupInvoice } from '@affiliates/components';
import { GetPaymentGroupLogEntries_paymentGroupLogEntries as IPaymentGroupLog } from '@affiliates/queries/types/GetPaymentGroupLogEntries';
import { DataFormat, formatValue } from '@affiliates/utils/formatValue';
import { PAYMENT_APP_ID } from '@frontend/app/constants/applicationIds';
import { DateFilter, DateRangeOptions, IDateRangeSettings } from '@frontend/app/components';
import { useDateFilterSettings, useMapDateRangeToVariables } from '@frontend/app/hooks';
// Removed unused imports
import { useApolloClient } from '@apollo/client';
import { GET_DASHBOARD_STATS_QUERY, GET_OFFER_BY_ID_WITH_STATS_QUERY, } from '@affiliates/queries';
import { roundToNearestCent } from '@affiliates/utils';
import { AFFILIATE_PAYMENTS_OWED } from '@affiliates/queries';
import { AffiliatePaymentsOwed, AffiliatePaymentsOwedVariables } from '@affiliates/queries/types/AffiliatePaymentsOwed';
import { isEmpty as isEmptyLodash, uniq, map } from 'lodash';
import { format } from 'date-fns';
import { logger } from '@common';

import styles from './PaymentOverview.scss';
// eslint-disable-next-line import/order
import menuStyles from '@frontend/app/components/DateFilter/DateMenu.scss';
import { TPaymentsDue, useSTAPaymentAppContext } from '../../contexts';

const { Link, Title, Text } = Typography;
const paymentsIcon = require('@frontend/app/assets/svgs/payment_circle.svg');

const { useState, useMemo, useEffect } = React;

interface IPaymentsOverview {
  clientId: string;
  clientName: string;
  dateFilter: IDateRangeSettings;
  earliestDate: Date | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose: any;
  visible: boolean;
  pendingPaymentGroups: IPaymentGroupLog[];
  paidPaymentGroups: IPaymentGroupLog[];
  balance: number;
  totalPaymentsDue: TPaymentsDue;
  forOneOffer: boolean;
  isMakeAPaymentLoading: boolean;
  makeAPayment: () => void;
  // Add new props for payment flow
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaymentFlowStart?: (newPayments: any[], paymentGroupName: string) => void;
}

export const PaymentsOverview: React.FC<Readonly<IPaymentsOverview>> = React.memo((props) => {
  const history = useHistory();
  const [paymentInvoicePaymentGroupId, setPaymentInvoicePaymentGroupId] = useState<number>();
  const [isPaymentInvoiceOpen, setIsPaymentInvoiceOpen] = useState(false);

  // Create isolated date filter that doesn't affect parent context
  const localDateFilter = useDateFilterSettings(props.dateFilter.selectedOption);

  // Get context for offers and other data
  const { offers, paymentsDue: contextPaymentsDue, setPaymentsDue } = useSTAPaymentAppContext();
  const client = useApolloClient();

  // Local state for payments due when using isolated date filter
  const [localPaymentsDue, setLocalPaymentsDue] = useState<TPaymentsDue | null>(null);

  // Convert local date filter to variables for client-side filtering
  const localDateRangeVariables = useMapDateRangeToVariables(localDateFilter.dateRange);

  // Convert parent date filter to variables for comparison
  const parentDateRangeVariables = useMapDateRangeToVariables(props.dateFilter.dateRange);

  // Filter helper function to check if a payment group falls within the date range
  const isWithinDateRange = (paymentGroup: IPaymentGroupLog, startDate: string | null, endDate: string | null) => {
    if (!paymentGroup.externalDateCreated) {
      return true; // Include payments without dates
    }

    const paymentDate = new Date(paymentGroup.externalDateCreated);

    if (startDate) {
      const start = new Date(startDate);
      if (paymentDate < start) {
        return false;
      }
    }

    if (endDate) {
      const end = new Date(endDate);
      if (paymentDate > end) {
        return false;
      }
    }

    return true;
  };

  // Process payment groups with client-side date filtering
  const [localPendingPaymentGroups, localPaidPaymentGroups] = useMemo(() => {
    // Use parent's payment groups data
    const allPendingGroups = props.pendingPaymentGroups || [];
    const allPaidGroups = props.paidPaymentGroups || [];

    // Apply client-side date filtering
    const filteredPending = allPendingGroups.filter((group) =>
      isWithinDateRange(group, localDateRangeVariables.startDate, localDateRangeVariables.endDate)
    );
    const filteredPaid = allPaidGroups.filter((group) =>
      isWithinDateRange(group, localDateRangeVariables.startDate, localDateRangeVariables.endDate)
    );

    return [filteredPending, filteredPaid];
  }, [props.pendingPaymentGroups, props.paidPaymentGroups, localDateRangeVariables.startDate, localDateRangeVariables.endDate]);

  // Track option + concrete date range to detect changes (including Custom date changes)
  const [prevDateSignature, setPrevDateSignature] = useState(
    `${localDateFilter.selectedOption}|${localDateRangeVariables.startDate ?? ''}|${localDateRangeVariables.endDate ?? ''}`
  );

  // Refetch offer data with new date range to update payments due
  useEffect(() => {
    if (!props.visible || offers.length === 0) {
      return;
    }

    // Create current date signature to detect any changes (option or date range)
    const currentDateSignature = `${localDateFilter.selectedOption}|${localDateRangeVariables.startDate ?? ''}|${localDateRangeVariables.endDate ?? ''}`;

    // Check if date filter has actually changed (including Custom date changes)
    if (prevDateSignature === currentDateSignature) {
      return;
    }

    setPrevDateSignature(currentDateSignature);

    // If switching back to parent's date filter (All Time), clear local state
    if (localDateFilter.selectedOption === props.dateFilter.selectedOption &&
        localDateRangeVariables.startDate === parentDateRangeVariables.startDate &&
        localDateRangeVariables.endDate === parentDateRangeVariables.endDate) {
      setLocalPaymentsDue(null);
      return;
    }

    const fetchOfferData = async () => {
      try {
        // Only fetch offer-specific data if we're viewing a single offer
        if (props.forOneOffer && offers.length === 1) {
          const result = await client.query({
            query: GET_OFFER_BY_ID_WITH_STATS_QUERY,
            variables: {
              id: offers[0].id, // Use the first offer ID from context
              startDate: localDateRangeVariables.startDate,
              endDate: localDateRangeVariables.endDate,
            },
            fetchPolicy: 'network-only',
          });

          if (result.data?.offer) {
            const { promoStats, linkStats, cashbackEarned, cashbackPaid } = result.data.offer;
            const offer = result.data.offer.offer;

            // Calculate payments due using the same logic as in useRefreshOfferDetailsQueries
            // Store in LOCAL state instead of global context to avoid re-renders
            if (offer.isPromoLink && promoStats && linkStats) {
              setLocalPaymentsDue({
                paymentsDue: Math.max(
                  0,
                  roundToNearestCent(
                    promoStats.payoutEarned - promoStats.payoutMade +
                    (linkStats.payoutEarned - linkStats.payoutMade) +
                    ((cashbackEarned || 0) - (cashbackPaid || 0))
                  ),
                ),
                state: 'ready',
              });
            } else if (promoStats) {
              setLocalPaymentsDue({
                paymentsDue: Math.max(
                  0,
                  roundToNearestCent(
                    promoStats.payoutEarned - promoStats.payoutMade +
                    ((cashbackEarned || 0) - (cashbackPaid || 0))
                  )
                ),
                state: 'ready',
              });
            } else if (linkStats) {
              setLocalPaymentsDue({
                paymentsDue: Math.max(
                  0,
                  roundToNearestCent(
                    linkStats.payoutEarned - linkStats.payoutMade +
                    ((cashbackEarned || 0) - (cashbackPaid || 0))
                  )
                ),
                state: 'ready',
              });
            }
          }
        } else {
          // For dashboard view, fetch dashboard statistics with the local date filter
          const dashboardResult = await client.query({
            query: GET_DASHBOARD_STATS_QUERY,
            variables: {
              startDate: localDateRangeVariables.startDate,
              endDate: localDateRangeVariables.endDate,
              offerType: null, // or appropriate default
              selectedFilters: null, // or appropriate default
              withMemberCount: false,
            },
            fetchPolicy: 'network-only',
          });

          if (dashboardResult.data?.summary) {
            const { payoutEarned, payoutMade } = dashboardResult.data.summary;
            // Calculate payments due as difference between earned and made
            const paymentsDueAmount = Math.max(0, roundToNearestCent((payoutEarned || 0) - (payoutMade || 0)));

            // Update the global context with the new payments due
            setPaymentsDue({
              paymentsDue: paymentsDueAmount,
              state: 'ready',
            });
            // Clear local state so it uses the updated context
            setLocalPaymentsDue(null);
          }
        }
      } catch (error) {
        setLocalPaymentsDue({ state: 'failed', errorMessage: 'Failed to load payment data' });
      }
    };

    fetchOfferData();
  }, [localDateFilter.selectedOption, props.visible, offers, client, localDateRangeVariables, parentDateRangeVariables, props.dateFilter.selectedOption, prevDateSignature, props.forOneOffer, setPaymentsDue]);

  // Custom payment flow initialization using local date filter
  const [isLocalMakeAPaymentLoading, setIsLocalMakeAPaymentLoading] = useState(false);

  // Reset date filter to All Time when drawer reopens after payment completion
  useEffect(() => {
    if (props.visible && localDateFilter.selectedOption !== DateRangeOptions.ALL_TIME) {
      // Reset to All Time when drawer opens
      localDateFilter.onChange(DateRangeOptions.ALL_TIME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.visible]); // Only depend on visibility change

  const localInitializePaymentFlow = useMemo(() => async () => {
    if (isEmptyLodash(offers)) {
      return;
    }

    setIsLocalMakeAPaymentLoading(true);
    try {
      const offerIds = uniq(map(offers, 'id'));
      const newPaymentsResult = await client.query<AffiliatePaymentsOwed, AffiliatePaymentsOwedVariables>({
        fetchPolicy: 'no-cache',
        query: AFFILIATE_PAYMENTS_OWED,
        variables: {
          endDate: localDateRangeVariables.endDate || null,
          offerIds,
          startDate: localDateRangeVariables.startDate || null,
        },
      });

      const generatedNewPayments = map(newPaymentsResult.data.affiliatePaymentsOwed, (payment) => {
        const {
          metadata: {
            affiliateOfferLinkId,
            affiliateOfferPromoId,
            ...remainingMetadata
          },
          ...remainingPaymentProps
        } = payment;

        if (affiliateOfferLinkId) {
          return {
            ...remainingPaymentProps,
            metadata: {
              ...remainingMetadata,
              affiliateOfferLinkId,
            },
          };
        }
        if (affiliateOfferPromoId) {
          return {
            ...remainingPaymentProps,
            metadata: {
              ...remainingMetadata,
              affiliateOfferPromoId,
            },
          };
        }

        throw new Error('Payment missing both affiliate offer link and affiliate offer promo.');
      });

      const paymentGroupName = format(new Date(), 'MMM dd, yyyy');

      // Call the parent's make payment function with our generated data
      if (props.onPaymentFlowStart) {
        props.onPaymentFlowStart(generatedNewPayments, paymentGroupName);
      } else {
        // Avoid calling the original function to prevent context updates
      }
    } catch (err) {
      logger.error(err);
    }
    setIsLocalMakeAPaymentLoading(false);
  }, [offers, localDateRangeVariables, client, props]);

  const header = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <div className={(styles as any).header}>
      <Row justify="center">
        <img src={paymentsIcon} />
      </Row>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Row justify="center" className={(styles as any).paymentOverviewTitle}>
        <Title level={4}>Payments Overview</Title>
      </Row>
    </div>
  );

  const renderPaymentGroupRow = (paymentGroupLog: IPaymentGroupLog, index: number) => {
    const onClick = () => {
      if (isNull(paymentGroupLog.externalId)) {
        return;
      }
      setPaymentInvoicePaymentGroupId(paymentGroupLog.externalId);
      setIsPaymentInvoiceOpen(true);
    };

    return (
      <div key={`${index}.${paymentGroupLog.status}`}>
        <Row className={styles.paymentEntry} justify="space-between" align="middle">
          <Col>{paymentGroupLog.name}</Col>
          <Col>
            $
            {formatValue(DataFormat.Money, paymentGroupLog.totalAmount)}
            <span>
              {!isNull(paymentGroupLog.externalId) && (
                <Button
                  style={{
                    padding: '0px 4px',
                    marginLeft: '4px',
                    color: '#3996e0',
                    height: '20px',
                  }}
                  type="text"
                  shape="round"
                  icon={<ArrowRightIcon />}
                  onClick={onClick}
                />
              )}
            </span>
          </Col>
        </Row>
        <Divider className={styles.titleDivider} />
      </div>
    );
  };

  // Use local filtered data and context data
  const effectivePaymentsDue = localPaymentsDue || contextPaymentsDue || props.totalPaymentsDue;
  const effectivePendingGroups = localPendingPaymentGroups;
  const effectivePaidGroups = localPaidPaymentGroups;
  const effectiveBalance = props.balance;

  const disableMakePaymentsButton = (
    effectivePaymentsDue.state !== 'ready' || effectivePaymentsDue.paymentsDue === 0
  );
  let dollarAmount = null;
  switch (effectivePaymentsDue.state) {
    case 'loading':
    case 'failed':
      dollarAmount = (
        <Spinner indicator={<LoadingOutlined spin />} />
      );
      break;
    case 'ready':
      dollarAmount = formatValue(DataFormat.Money, effectivePaymentsDue.paymentsDue);
      break;
  }

  return (
    <>
      <Drawer
        destroyOnClose={false}
        title={header}
        onClose={props.onClose}
        visible={props.visible}
        className={styles.PaymentOverview}
        width="450px"
        mask={false}
      >
        <Row className={styles.spaceBottom} justify="space-between" align="middle">
          <Col className={styles.subtitle1}>
            <Title level={5}>Payments Due</Title>
          </Col>
          <Col className={styles.smallText}>
            <Text>
              Balance: $
              {formatValue(DataFormat.Money, effectiveBalance)}
            </Text>
          </Col>
        </Row>

        <Row justify="space-between" align="middle">
          <Col className={styles.subtitle1} span={24}>
            <DateFilter
              buttonClassName={styles.dateFilterButton}
              menuClassName={menuStyles.paymentsOverviewDateMenu}
              earliestDate={props.earliestDate}
              settings={localDateFilter}
            />
          </Col>
        </Row>

        <div className={styles.paymentBalance}>
          <Row justify="center" className={styles.cost}>
            $
            {dollarAmount}
          </Row>
          <Row justify="center" className={styles.caption}>
            <Text>{props.forOneOffer ? 'Payments Due from Offer' : 'Total Payments Due'}</Text>
          </Row>
        </div>

        <Row justify="center">
          <Button
            type="primary"
            onClick={localInitializePaymentFlow}
            disabled={disableMakePaymentsButton}
            loading={isLocalMakeAPaymentLoading}
          >
            Make a Payment
          </Button>
        </Row>

        <Divider />

        <div className={styles.paymentLog}>
          <div className={styles.paymentLogHeader}>
            <Row justify="space-between">
              <Col className={styles.paymentLogTitle}>
                <Title level={5}>Payment History</Title>
              </Col>
              <Col className={styles.smallText}>
                <Link onClick={() => history.push(`/app/${PAYMENT_APP_ID}`)}>
                  View History
                </Link>
              </Col>
            </Row>
          </div>

          <Row align="middle" justify="start">
            <Col><EllipsisIcon /></Col>
            <Col><Text className={styles.title}>pending</Text></Col>
            <Divider className={styles.titleDivider} />
          </Row>

          {effectivePendingGroups.length === 0
            && <Row className={styles.noPayment}><Text>No Payments are pending</Text></Row>}

          {effectivePendingGroups
            && effectivePendingGroups.map((paymentGroupLog, idx) => renderPaymentGroupRow(paymentGroupLog, idx))}

          <Row className={styles.paymentLog} align="middle" justify="start">
            <Col><CheckIcon /></Col>
            <Col><Text className={styles.title}>Paid</Text></Col>
            <Divider className={styles.titleDivider} />
          </Row>

          {effectivePaidGroups.length === 0
            && <Row className={styles.noPayment}><Text>No Payments have been made</Text></Row>}

          {effectivePaidGroups
            && effectivePaidGroups.map((paymentGroupLog, idx) => renderPaymentGroupRow(paymentGroupLog, idx))}
        </div>

      </Drawer>
      <STAPaymentGroupInvoice
        clientId={props.clientId}
        clientName={props.clientName}
        paymentGroupId={paymentInvoicePaymentGroupId}
        visible={isPaymentInvoiceOpen}
        onClose={() => setIsPaymentInvoiceOpen(false)}
      />
    </>
  );
});

PaymentsOverview.displayName = 'PaymentsOverview';
